/**
 * CheckDetailForm component
 * --------------------------------------------------------------------
 * ✅ Mục đích:
 *   - Hiển thị danh sách các dòng câu hỏi / tiêu chí kiểm tra (dạng form list)
 *   - Cho phép chọn các loại input khác nhau (radio, select, checkbox, rank)
 *   - Hiển thị ô ghi chú chi tiết (TextArea) khi chọn các giá trị cụ thể (triggerValues)
 *   - Giữ nguyên cột label bên trái giống cấu trúc Table
 *
 * --------------------------------------------------------------------
 * 🔧 Props chính:
 * - listName:        (string | array) Tên Form.List
 * - data:            (array) Danh sách các dòng hiển thị, mỗi dòng là 1 object
 * - columns:         (array) Các cột bổ sung, thường dùng để hiển thị label
 * - requiredColWidth:(string) Chiều rộng cột lựa chọn chính (radio / select / ...)
 * - placeholder:     (string) Placeholder cho TextArea
 * - radioLabels:     (object) { yes: "Có", no: "Không (ghi rõ)" } (áp dụng cho inputType='radio')
 * - triggerValues:   (array) Danh sách giá trị sẽ hiển thị TextArea khi chọn (ví dụ ['no'])
 *
 * --------------------------------------------------------------------
 * 🧩 Cấu trúc dữ liệu `data` (mỗi dòng)
 * {
 *   key: "unique_id",
 *   label: "Tên hiển thị bên trái",
 *   inputType: "radio" | "rank" | "select" | "checkbox",  // Loại input
 *   options: [                                            // Dùng cho rank/select/checkbox
 *     { value: "hang_1", label: "Hạng 1" },
 *     { value: "hang_2", label: "Hạng 2" },
 *     ...
 *   ],
 *   triggerValues: ["no" hoặc "khac" hoặc "hang_3"],     // Khi chọn các giá trị này -> hiển thị TextArea
 *   has_occurrence: "yes" | "no" | "hang_2" | [],         // Giá trị khởi tạo
 *   occurrence_detail: ""                                 // Ghi chú chi tiết (TextArea)
 * }
 *
 * --------------------------------------------------------------------
 * 💡 Các ví dụ sử dụng:
 *
 * 1️⃣ Radio (mặc định)
 * <CheckDetailForm
 *   listName="medical_history"
 *   data={[
 *     { label: "Dị ứng", inputType: "radio", has_occurrence: "no" },
 *     { label: "Bản thân", inputType: "radio", has_occurrence: "no" },
 *   ]}
 *   radioLabels={{ yes: "Có, ghi rõ", no: "Không" }}
 * />
 *
 * 2️⃣ Rank (radio nhiều lựa chọn)
 * <CheckDetailForm
 *   listName="rang_3_trai"
 *   data={[
 *     {
 *       label: "Răng 3 (bên trái)",
 *       inputType: "rank",
 *       options: [
 *         { value: "hang_1", label: "Hạng 1" },
 *         { value: "hang_2", label: "Hạng 2" },
 *         { value: "hang_3", label: "Hạng 3" },
 *       ],
 *       triggerValues: ["hang_3"], // Khi chọn Hạng 3 -> hiển thị TextArea
 *     },
 *   ]}
 * />
 *
 * 3️⃣ Select
 * <CheckDetailForm
 *   listName="loai_can_lech"
 *   data={[
 *     {
 *       label: "Loại cắn lệch",
 *       inputType: "select",
 *       options: [
 *         { value: "trai", label: "Cắn lệch trái" },
 *         { value: "phai", label: "Cắn lệch phải" },
 *         { value: "giua", label: "Cắn giữa" },
 *         { value: "khac", label: "Khác" },
 *       ],
 *       triggerValues: ["khac"], // Khi chọn "Khác" -> hiển thị TextArea
 *     },
 *   ]}
 * />
 *
 * 4️⃣ Checkbox
 * <CheckDetailForm
 *   listName="dac_diem_rang"
 *   data={[
 *     {
 *       label: "Đặc điểm răng",
 *       inputType: "checkbox",
 *       options: [
 *         { label: "Răng xoay", value: "xoay" },
 *         { label: "Răng chen chúc", value: "chen_chuc" },
 *         { label: "Khác", value: "khac" },
 *       ],
 *       triggerValues: ["khac"], // Khi tick “Khác” -> hiển thị TextArea
 *     },
 *   ]}
 * />
 *
 * --------------------------------------------------------------------
 * 💬 Lưu ý:
 * - Cột label được truyền qua props `columns` để hiển thị như trong ví dụ gốc.
 * - Mỗi hàng tự động xử lý Form.Item với key dạng [index, fieldName].
 * - Tương thích hoàn toàn với Ant Design Form.List.
 * --------------------------------------------------------------------
 */

import React from 'react';
import { Form, Input, Radio, Table, Select, Checkbox } from 'antd';

const CheckDetailForm = (props) => {
    const {
        listName,
        data,
        columns = [],
        requiredColWidth = '300px',
        placeholder = "Ghi rõ chi tiết cụ thể tại đây",
        radioLabels = { no: "Không", yes: "Có (ghi rõ chi tiết)" },
        triggerValues = ['yes'], // Giá trị mặc định sẽ hiển thị TextArea
        required = false,
        ...rest
    } = props;

    // Theo dõi toàn bộ list values trong Form (tự re-render khi form thay đổi)
    const listValues = Form.useWatch(listName) || [];
    const form = Form.useFormInstance();

    // Helper: kiểm tra có cần hiển thị detail không ở index i
    const shouldShowDetail = (record, i) => {
        const row = listValues?.[i] || {};
        const value = row.has_occurrence ?? record.has_occurrence;
        const detail = (row.occurrence_detail ?? record.occurrence_detail) || '';
        const triggers = record.triggerValues || triggerValues;

        const byTrigger = Array.isArray(value)
            ? value?.some(v => triggers.includes(v))
            : triggers.includes(value);

        const byDetail = String(detail).trim() !== '';

        return byTrigger || byDetail;
    };

    /**
     * Xử lý khi giá trị thay đổi cho tất cả loại input
     */
    const handleValueChange = (index, value, record) => {
        const triggers = record.triggerValues || triggerValues;
        const currentList = form.getFieldValue(listName) || [];

        // Nếu người dùng clear (bỏ chọn) → null
        if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
            currentList[index] = { has_occurrence: null, occurrence_detail: null };
        } else {
            const isTriggered = Array.isArray(value)
                ? value.some(v => triggers.includes(v))
                : triggers.includes(value);

            if (!isTriggered) {
                // Nếu chọn giá trị không nằm trong trigger → xóa detail
                currentList[index] = { has_occurrence: value, occurrence_detail: null };
            } else {
                // Nếu có trigger → giữ lại giá trị
                const prev = currentList[index] || {};
                currentList[index] = { ...prev, has_occurrence: value };
            }
        }

        form.setFieldsValue({ [listName]: currentList });
    };

    // const handleValueChange = (index, value, record) => {
    //     const triggers = record.triggerValues || triggerValues;
    //     const isTriggered = Array.isArray(value)
    //         ? value.some(v => triggers.includes(v))
    //         : triggers.includes(value);

    //     if (!isTriggered) {
    //         // Khi người dùng chọn giá trị không thuộc triggerValues,
    //         // ẩn TextArea và xóa nội dung detail trong form
    //         const currentList = form.getFieldValue(listName) || [];
    //         if (currentList[index]) {
    //             currentList[index].occurrence_detail = '';
    //             form.setFieldsValue({ [listName]: currentList });
    //         }
    //     }
    // };

    /**
     * Hàm render linh hoạt cho từng loại input
     */
    const renderInputType = (record, index) => {
        const { inputType = "radio", options = [] } = record;

        switch (inputType) {
            case "rank":
                return (
                    <Form.Item
                        name={[index, "has_occurrence"]}
                        rules={[{ required: required, message: "Bắt buộc chọn" }]}
                    >
                        <Radio.Group onChange={(e) => handleValueChange(index, e.target.value, record)}>
                            {options.map((opt) => (
                                <Radio key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </Form.Item>
                );

            case "select":
                return (
                    <Form.Item
                        name={[index, "has_occurrence"]}
                        rules={[{ required: required, message: "Bắt buộc chọn" }]}
                    >
                        <Select
                            placeholder="Chọn một lựa chọn"
                            onChange={(value) => handleValueChange(index, value, record)}
                            allowClear
                        >
                            {options.map((opt) => (
                                <Select.Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                );

            case "checkbox":
                return (
                    <Form.Item
                        name={[index, "has_occurrence"]}
                        rules={[{ required: required, message: "Bắt buộc chọn" }]}
                    >
                        <Checkbox.Group
                            options={options}
                            onChange={(values) => handleValueChange(index, values, record)}
                        />
                    </Form.Item>
                );

            case "radio":
            default:
                return (
                    <Form.Item
                        name={[index, "has_occurrence"]}
                        initialValue={record.has_occurrence}
                        rules={[{ required: required, message: "Bắt buộc chọn" }]}
                    >
                        <Radio.Group onChange={(e) => handleValueChange(index, e.target.value, record)}>
                            <Radio value="no">{radioLabels.no}</Radio>
                            <Radio value="yes">{radioLabels.yes}</Radio>
                        </Radio.Group>
                    </Form.Item>
                );
        }
    };

    /**
     * Cột lựa chọn chính (radio/select/checkbox)
     */
    const requiredColumns = [
        {
            title: "",
            dataIndex: "has_occurrence",
            width: requiredColWidth,
            className: "occurrence-detail",
            render: (_, record, index) => renderInputType(record, index),
        },
    ];

    /**
     * Cột chi tiết (hiển thị TextArea nếu có trigger)
     */
    const occurrenceDetailColumn = {
        title: "",
        dataIndex: "occurrence_detail",
        className: "occurrence-detail",
        render: (_, record, index) =>
            shouldShowDetail(record, index) && (
                <Form.Item
                    name={[index, "occurrence_detail"]}
                    initialValue={record.occurrence_detail}
                    rules={[{ required: required, message: "Bắt buộc nhập" }]}
                >
                    <Input.TextArea rows={1} placeholder={placeholder} />
                </Form.Item>
            ),
    };

    // Giữ lại cột label do người dùng truyền vào
    const filteredColumns = columns.filter(col => col.dataIndex !== "occurrence_detail");

    // Hợp nhất toàn bộ cột
    let mergedColumns = [
        ...filteredColumns,
        ...requiredColumns,
        occurrenceDetailColumn,
    ];

    // Thêm className "occurrence-detail" cho tất cả
    mergedColumns = mergedColumns.map(col => ({
        ...col,
        className: col.className
            ? `${col.className} occurrence-detail`
            : "occurrence-detail",
    }));

    return (
        <Form.List name={listName}>
            {() => (
                <Table
                    className="no-border-table"
                    showHeader={false}
                    dataSource={data}
                    columns={mergedColumns}
                    rowKey={(record, index) => index}
                    pagination={false}
                    size="small"
                    bordered={false}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: "Chưa có dữ liệu" }}
                    {...rest}
                />
            )}
        </Form.List>
    );
};

export default CheckDetailForm;